import React, { ChangeEvent } from 'react';
import { DatePicker, Form, Typography, Col, Space } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import dayjs, { Dayjs } from 'dayjs';
import { FieldConfig } from '../../types/fieldTypes';

const { Text } = Typography;

interface DateFieldProps {
  field: FieldConfig;
  value: any;
  onChange: (e: ChangeEvent<HTMLInputElement>) => void;
  mode: 'edit' | 'view';
  formData?: Record<string, any>;
}

export const DateField: React.FC<DateFieldProps> = ({
  field,
  value,
  onChange,
  mode,
  formData = {},
}) => {
  // ✅ Handle date selection and maintain a consistent YYYY-MM-DD format
  const handleDateChange = (date: Dayjs | null) => {
    const formattedValue = date ? date.format('YYYY-MM-DD') : '';
    const syntheticEvent = {
      target: {
        name: field.name,
        value: formattedValue,
      },
    } as ChangeEvent<HTMLInputElement>;

    onChange(syntheticEvent);
  };

  // ✅ Ensure date parsing supports multiple formats to prevent disappearing values
  const parsedValue =
    value && typeof value === 'string'
      ? dayjs(value, ['YYYY-MM-DD', 'DD/MM/YYYY'], true)
      : null;

  // ✅ View mode (non-editable)
  if (mode === 'view') {
    return (
      <Col span={field.layout?.span || 24}>
        <Form.Item label={<Text type="secondary">{field.label}</Text>}>
          <DatePicker
            value={parsedValue && parsedValue.isValid() ? parsedValue : null}
            disabled
            format="DD/MM/YYYY"
            style={{ width: '100%' }}
          />
        </Form.Item>
      </Col>
    );
  }

  const colProps = {
    span: field.layout?.span || 24,
    offset: field.layout?.offset || 0,
    order: field.layout?.order,
  };

  return (
    <Col {...colProps} className={field.layout?.className}>
      <Form.Item
        label={field.label}
        required={field.required}
        help={
          field.customProps?.helpText && (
            <Text type="secondary">
              <InfoCircleOutlined style={{ marginRight: 4 }} />
              {field.customProps.helpText}
            </Text>
          )
        }
      >
        <DatePicker
          placeholder={
            field.customProps?.placeholder || field.placeholder || 'Select date'
          }
          value={parsedValue && parsedValue.isValid() ? parsedValue : null}
          onChange={handleDateChange}
          format="DD/MM/YYYY"
          disabled={field.customProps?.disabled}
          size="large"
          style={{ width: '100%' }}
          {...(field.customProps &&
            Object.fromEntries(
              Object.entries(field.customProps).filter(
                ([key]) =>
                  ![
                    'onFieldChange',
                    'onFormChange',
                    'helpText',
                    'disabled',
                    'disabledDate',
                  ].includes(key)
              )
            ))}
        />
      </Form.Item>
    </Col>
  );
};
